import React, {useEffect, useReducer} from "react";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import NumberFormat from "react-number-format";

import ai from "../../helpers/axios";
import TextAreaInput from "../textarea-input";
import * as Sentry from "@sentry/react";
import shortenName from "../../helpers/shorten-name";
import VendorAutocomplete from "./vendor-autocomplete";
import { LoadingButton } from "../shared/loading-button";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
      };
    }

    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }

    case "openDeleteModal": {
      return {
        ...state,
        showDeleteModal: true,
        ...(action.payload || {}),
      };
    }

    case "closeModal": {
      return {
        ...state,
        showModal: false,
        showDeleteModal: false,
        _id: null,
        vendor: null,
        amount: null,
        note: "",
      };
    }

    default:
      break;
  }
  return state;
};

const GroundTransportationModule = (props) => {
  const { getAccessTokenSilently } = useAuth0();

  let initialState = {
    showModal: false,
    showDeleteModal: false,
    saving: false,
    _id: null,
    vendor: null,
    amount: null,
    note: "",
    groundTransportationNotes: props.groundTransportationNotes,
    loading: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    props.setUnsavedChanges?.({
      section: 'groundTransportationModal',
      value: !!state.showModal
    });
  }, [props.setUnsavedChanges, state.showModal]);

  const handleAddGroundTransportationItem = (e) => {
    e.preventDefault();

    dispatch({
      type: "openModal",
    });
  };

  const handleEditGroundTransportationItem = (e, index) => {
    e.preventDefault();

    const item = state.groundTransportationNotes[index];
    if (!item) {
      return;
    }

    dispatch({
      type: "openModal",
      payload: {
        ...item,
        vendor: item.vendor ? {
          value: item.vendor.id,
          label: item.vendor.name,
        } : null,
      },
    });
  };

  const handleDeleteGroundTransportationItem = (e, index) => {
    e.preventDefault();

    const item = state.groundTransportationNotes[index];
    if (!item) {
      return;
    }

    dispatch({
      type: "openDeleteModal",
      payload: item,
    });
  };

  const handleCloseModal = () => {
    dispatch({
      type: "closeModal",
    });
  };

  const handleInputChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  };

  const handleAmountFieldChange = (values) => {
    const { value } = values;

    dispatch({
      type: "fieldChanged",
      field: "amount",
      value: value,
      fieldType: "float",
    });
  };

  const handleVendorFieldChange = (selectedVendor) => {
    dispatch({
      type: "fieldChanged",
      field: "vendor",
      value: selectedVendor,
    });
  };

  const handleSubmitGroundTransportationItem = async (e) => {
    e.preventDefault();

    if (!state.vendor || !state.amount || !state.note) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const data = {
      vendor: state.vendor ? {
        id: state.vendor?.value,
        name: state.vendor?.label,
      } : null,
      amount: state.amount,
      noteText: state.note,
    };
    let response;
    const token = await getAccessTokenSilently();
    if (state._id) {
      response = await ai.auth(token).put(
        `/api/trips/${props.tripId}/updateGroundTransportationNote/${state._id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } else {
      response = await ai.auth(token).post(
        `/api/trips/${props.tripId}/addGroundTransportationNote`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      handleCloseModal();

      props.parentDispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }
  };

  const handleDeleteGroundTransportationItemConfirm = async (e) => {
    e.preventDefault();

    if (!state._id) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const token = await getAccessTokenSilently();
    const response = await ai.auth(token).delete(
      `/api/trips/${props.tripId}/deleteGroundTransportationNote/${state._id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    ).catch((error) => {
      Sentry.captureException(error);
    });

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      handleCloseModal();

      props.parentDispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }
  };

  return (
    <>
      <div
        className="trip-notes-module"
        key={`key-ground-transportation-${props.dataDownloadedTimestamp.toString()}`}
        id="trip-groundTransportation"
      >
        <h5 className="mb-1 d-flex">
          Ground Transportation

          {!props.readOnly && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={handleAddGroundTransportationItem}
              title="Add"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <div className="trip-notes-table pb-0">
          <table>
            <thead>
            <tr>
              <th>Created By</th>
              <th>Vendor</th>
              <th>Amount</th>
              <th>Note</th>
              <th className="text-align-right">Actions</th>
            </tr>
            </thead>
            <tbody>
            {!state.groundTransportationNotes?.length && <tr>
              <td colSpan={4}>No ground transportations are saved yet</td>
              <td/>
            </tr>}
            {state.groundTransportationNotes?.map((note, index) => (
              <tr key={note._id}>
                <td>{shortenName(note.createdBy.name)}</td>
                <td>{note.vendor?.name || "-"}</td>
                <td>{note.amount ? note.amount.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }) : "-"}</td>
                <td>{note.note || "-"}</td>

                <td>
                  {!props.readOnly && (
                    <div className="d-flex justify-content-end">
                      <button
                        className="base-button icon-button icon-button-small evo-blue"
                        onClick={(e) => {
                          handleEditGroundTransportationItem(e, index);
                        }}
                        title="Edit"
                      >
                        <span className="material-icons">edit</span>
                      </button>

                      <button
                        className="base-button icon-button icon-button-small btn-danger ml-2"
                        onClick={(e) => handleDeleteGroundTransportationItem(e, index)}
                        title="Delete"
                      >
                        <span className="material-icons">delete</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state._id ? "Edit" : "Add"} Ground Transportation</h4>
        </div>
        <div className="modal-body edit-note-modal">
          <form onSubmit={handleSubmitGroundTransportationItem} className="w-100 base-form mt-20">
            <VendorAutocomplete
              type="groundTransportation"
              selectedVendor={state.vendor}
              handleChange={handleVendorFieldChange}
              required
            />

            <div className="form-group">
              <label htmlFor="amount" className="base-label">
                Amount <span className="required">*</span>
              </label>

              <NumberFormat
                value={state.amount}
                thousandSeparator={true}
                prefix="$"
                onValueChange={handleAmountFieldChange}
                placeholder="Amount"
                className="semibold-text"
                decimalScale={2}
                name="amount"
                allowNegative={false}
              />
            </div>

            <TextAreaInput
              type="text"
              name="note"
              value={state.note}
              label="Note"
              placeholder="Add note"
              handleChange={handleInputChange}
              required
            />

            <div className="w-100 mt-20 d-flex gap-20">
              <LoadingButton className="base-button evo-blue flex-half" type="submit" disabled={state.saving}>
                Submit
              </LoadingButton>

              <button className="base-button btn-danger flex-half" type="button" onClick={handleCloseModal}
                      disabled={state.saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Ground Transportation</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This ground transportation record will be permanently deleted from the trip. Are you sure you want to
            continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <LoadingButton className="base-button btn-danger flex-half" type="button"
                    onClick={handleDeleteGroundTransportationItemConfirm}
                    isLoading={state.saving}>
              Delete
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default GroundTransportationModule;
